import React, { useEffect, useRef, useState } from 'react';
import { Button, Container, Form, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';

import { Empty, FormInput, Layout, Loader } from 'components';
import { engageWhite, invitation, office } from 'assets/icons';
import { AppConstants, ServerConstants } from 'constant';
import { useFetch } from 'hooks/fetch';
import EngageFunctionNewInvitation from './modal/new';
import { AppUtils, ModelUtils } from 'utils';
import EngageFunctionEditMemberData from './modal/edit';
import EngageFunctionDeleteMemberData from './modal/delete';
import EngageFunctionResendInvitation from './modal/resend';
import EngageFunctionWithdrawInvitation from './modal/withdraw';

const { PATH_ENGAGE_OFFICE, PATH_ENGAGE_LEARNING } = AppConstants;
const { API_METHOD_GET, API_URL_INVITATIONS } = ServerConstants;

const FunctionEngageScreen = () => {

  const { t } = useTranslation();
  const invitationFetch = useFetch(API_METHOD_GET);
  const [invitationsFiltered, setInvitationsFiltered] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [reload, setReload] = useState(0);
  const invitations = useRef([]);

  useEffect(() => {
    if (AppUtils.isUserOrganization()) {
      invitationFetch.setFetchUrl(API_URL_INVITATIONS);
      invitationFetch.forceFetchData();
    }
  }, []);

  useEffect(() => {
    if (reload > 0) {
      setClaimed(false);
      invitationFetch.forceFetchData();
    }
  }, [reload])

  useEffect(() => {
    if (invitationFetch.response) {
      invitations.current = invitationFetch.response.data.map(el => ModelUtils.convertInvitationModelB2F(el));
      setInvitationsFiltered(invitations.current.filter(el => el.accepted === claimed));
    }
  }, [invitationFetch.response]);


  useEffect(() => {
    let array = invitations.current.filter(el => el.accepted === claimed);
    if (searchTerm && searchTerm.trim() !== '') {
      array = array.filter(el => el.fullName.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    setInvitationsFiltered(array);
  }, [searchTerm, claimed]);

  return (
    <Layout>
      <div className="function-wallpaper engage">
        <Container style={{ height: '100%', width: '100%' }}>
          <div className="d-flex flex-direction-row justify-content-around align-items-center" style={{ height: '100%' }}>
            <Image src={office} className='d-none d-lg-block mr-5' />
            <div className='ml-5'>
              <div className="d-flex flex-direction-row align-items-center mb-3">
                <Image src={engageWhite} />
                <span className="ml-3">{t('functions.engage.title')}</span>
              </div>
              <h2>{t('functions.engage.header.title')}</h2>
              <p>{t('functions.engage.header.description')}</p>
              <Button variant='secondary' disabled={!AppUtils.isUserLogged()} onClick={() => window.location.href = PATH_ENGAGE_OFFICE} className='mr-2'>{t('functions.engage.header.action.virtual')}</Button>
              <Button variant='secondary' onClick={() => window.location.href = PATH_ENGAGE_LEARNING}>{t('learning.goto')}</Button>
            </div>
          </div>
        </Container>
      </div>
      {AppUtils.isUserOrganization() ?
        <Container className="mb-5">
          <EngageFunctionNewInvitation show={showNewModal} onDismiss={() => setShowNewModal(false)} onSubmit={() => { setShowNewModal(false); setReload(old => old + 1);}} />
          <div className="d-flex flex-direction-row align-items-center mt-5">
            <Image src={invitation} />
            <h3 className="text-blue mx-4 mb-0">{t('functions.engage.invitations.title')}</h3>
            <Button variant="info" className="px-3" onClick={() => setShowNewModal(true)}><FontAwesomeIcon icon={'plus'} /></Button>
          </div>
          {invitationFetch.loading ? <div className="mt-2"><Loader /></div> :
            invitations.current.length === 0 ? <Empty messageKey={t('functions.engage.invitations.empty.general')} /> :
              <>
                <div className="d-flex flex-direction-row justify-content-between align-items-center mb-3">
                  <div className="mb-3">
                    <div className="d-flex flex-direction-row align-items-center">
                      <span className={"pt-1" + (claimed === false ? " text-blue" : "")}>{t('functions.engage.invitations.claimed.no')}</span>
                      <Form.Switch
                        className="form-switch-blue mx-2"
                        onClick={() => setClaimed(old => !old)}
                        value={claimed}
                      />
                      <span className={"pt-1" + (claimed === true ? " text-blue" : "")}>{t('functions.engage.invitations.claimed.yes')}</span>
                    </div>
                  </div>
                  <FormInput id="searchTerm" placeholder={t('functions.engage.invitations.search')} onChange={(e) => setSearchTerm(e.target.value)} className="mb-0" />
                </div>
                <div className="mb-3">
                  {invitationsFiltered.length === 0 ? <Empty messageKey={t('functions.engage.invitations.empty.' + (claimed ? 'accepted' : 'invited'))} /> :
                    invitationsFiltered.map(el => claimed ? <ClaimedItem el={el} reload={() => setReload((old) => old + 1)} t={t} /> : <NotClaimedItem el={el} reload={() => setReload((old) => old + 1)} t={t} />)}
                </div>
              </>
          }
        </Container>
        : undefined}
    </Layout >
  )
}

const NotClaimedItem = ({ el, reload, t }) => {

  const [showResendModal, setShowResendModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  return (
    <div key={'invitation-' + el.invitationId}>
      <EngageFunctionResendInvitation el={el} show={showResendModal} onDismiss={() => setShowResendModal(false)} onSuccess={() => { setShowResendModal(false); reload() }} />
      <EngageFunctionWithdrawInvitation el={el} show={showWithdrawModal} onDismiss={() => { setShowWithdrawModal(false) }} onSuccess={() => { setShowWithdrawModal(false); reload() }} />
      <div className="invitation" >
        <div className="invitation-item">
          <span>{el.fullName}</span>
          <span className="mx-2">&#x2022;</span>
          <span>{el.position}</span>
          <span className="mx-2">&#x2022;</span>
          <span className="muted">{moment(el.date).utc(true).local().fromNow()}</span>
        </div>
        <div>
          <Button variant="secondary" className="mr-2" onClick={() => setShowResendModal(true)}>{t('functions.engage.invitations.resend.action')}</Button>
          <Button variant="danger" onClick={() => setShowWithdrawModal(true)}>{t('functions.engage.invitations.withdraw.action')}</Button>
        </div>
      </div>
    </div>
  )
}

const ClaimedItem = ({ el, reload, t }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <div key={'invitation-' + el.invitationId}>
      <EngageFunctionEditMemberData el={el} show={showEditModal} onDismiss={() => setShowEditModal(false) } onSuccess={() => { setShowEditModal(false); reload(); }} />
      <EngageFunctionDeleteMemberData el={el} show={showDeleteModal} onDismiss={() => setShowDeleteModal(false) } onSuccess={() => { setShowDeleteModal(false); reload(); }} />
      <div className="invitation">
        <div className="invitation-item">
          <span>{el.fullName}</span>
          <span className="mx-2">&#x2022;</span>
          <span>{el.position}</span>
          <span className="mx-2">&#x2022;</span>
          <span className="muted">{moment(el.date).utc(true).local().fromNow()}</span>
        </div>
        <div>
          <Button variant="secondary" className="mr-2" onClick={() => setShowEditModal(true)}>{t('functions.engage.invitations.edit.action')}</Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{t('functions.engage.invitations.delete.action')}</Button>
        </div>
      </div>
    </div>
  )
}


export default FunctionEngageScreen;